import React from "react";
import "./XenaDashboard.css";

const XenaDashboard = () => {
  return (
    <div className="dashboard-guide">
      <h1>XenaChat Dashboard</h1>

      <p>
        The <strong>XenaChat Dashboard</strong> is the centralized workspace for creating,
        managing, and optimizing AI-powered chatbots. It provides operational visibility,
        performance tracking, and access to platform resources - all within a unified interface.
      </p>
      <h2>Create New Bot</h2>
      <p>
        The bot creation module enables organizations to deploy AI assistants
        tailored to their business objectives, including customer support,
        knowledge automation, and lead engagement.
      </p>

      <ul>
        <li>
          <strong>Bot Initialization:</strong> Launch new chatbot instances within your workspace.
        </li>
        <li>
          <strong>Data Configuration:</strong> Upload and structure knowledge sources to train
          your bot effectively.
        </li>
        <li>
          <strong>Plan-Based Access:</strong> Bot creation capabilities are governed by
          subscription limits. Upgrade your plan to expand capacity if required.
        </li>
      </ul>

      <h2>Your Bots</h2>
      <p>
        The bot management section provides full administrative control over all
        chatbot instances within your account.
      </p>

      <ul>
        <li>
          <strong>Bot Overview:</strong> View all deployed bots and their operational status.
        </li>
        <li>
          <strong>Configuration Management:</strong> Modify behavior, appearance,
          and response logic.
        </li>
        <li>
          <strong>Workspace Organization:</strong> Move bots across workspaces
          where multi-workspace environments are enabled.
        </li>
        <li>
          <strong>Cloning & Duplication:</strong> Replicate bot configurations
          to accelerate deployment.
        </li>
      </ul>

      <h2>Usage Monitoring</h2>
      <p>
        Monitor resource consumption and subscription utilization in real time.
      </p>

      <ul>
        <li>
          <strong>Character Consumption:</strong> Track processed text volume
          against your plan limits.
        </li>
        <li>
          <strong>Bot Limits:</strong> View total active bots compared to
          your subscription allowance.
        </li>
      </ul>

      <h2>Inbox & Engagement Analytics</h2>
      <p>
        The Inbox provides insight into user interaction trends and communication
        volume across all deployed bots.
      </p>

      <ul>
        <li>
          <strong>User Activity:</strong> Monitor the number of unique users interacting with your bots.
        </li>
        <li>
          <strong>Conversation Tracking:</strong> Analyze conversation volume and engagement patterns.
        </li>
      </ul>

      <h2>Return on Investment (ROI)</h2>
      <p>
        The ROI dashboard estimates the operational and financial impact
        generated by your chatbot deployment.
      </p>

      <ul>
        <li>
          <strong>Cost Savings:</strong> Estimate operational expenses reduced
          through automation.
        </li>
        <li>
          <strong>Time Savings:</strong> Measure total time saved via automated
          interactions.
        </li>
      </ul>

      <h2>Main Navigation</h2>
      <p>
        The primary navigation menu provides access to core platform modules
        and administrative features.
      </p>

      <ul>
        <li>
          <strong>Home:</strong> Access the dashboard overview.
        </li>
        <li>
          <strong>API & Integrations:</strong> Configure API credentials and
          connect third-party messaging or business platforms.
        </li>
        <li>
          <strong>Help Center:</strong> Browse product documentation,
          onboarding guides, and troubleshooting resources.
        </li>
        <li>
          <strong>Contact Sales:</strong> Engage with the sales team for
          enterprise solutions and custom pricing.
        </li>
        <li>
          <strong>Submit Ticket:</strong> Create support requests for
          technical assistance.
        </li>
      </ul>

      <h2>Help Center</h2>
      <p>
        The Help Center delivers structured documentation and onboarding
        materials designed to accelerate product adoption.
      </p>

      <ul>
        <li>
          <strong>Search Functionality:</strong> Quickly locate relevant
          articles and setup instructions.
        </li>
        <li>
          <strong>Getting Started Guides:</strong> Step-by-step documentation
          for initial deployment.
        </li>
        <li>
          <strong>Platform Integrations:</strong> Connect bots with supported
          messaging channels and external systems.
        </li>
        <li>
          <strong>Account & Settings:</strong> Manage billing, subscription,
          and workspace configurations.
        </li>
      </ul>

      <h2>Sales & Support</h2>
      <p>
        XenaChat provides multiple channels for sales inquiries, product demonstrations,
        and general assistance.
      </p>

      <ul>
        <li>
          <strong>Demo Requests:</strong> Schedule a live platform walkthrough.
        </li>
        <li>
          <strong>Pricing Inquiries:</strong> Explore plans tailored to your organization.
        </li>
        <li>
          <strong>General Questions:</strong> Submit inquiries directly to the support team.
        </li>
        <li>
          <strong>Direct Contact:</strong> Reach the team during official business hours for immediate assistance.
        </li>
      </ul>

      <p>
        For written inquiries, complete the contact form with your professional
        details and business requirements. Our team will respond promptly.
      </p>
    </div>
  );
};

export default XenaDashboard;
